import { useCallback, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { ui } from '@procuros/datachecks';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { logError } from 'services/logging/logging';
import { WebEDIError } from 'services/webediErrors/types';
import { FileInput } from 'components/Form/FileInput/FileInput';
import { LoaderResult } from 'components/Form/FileInput/types';
import { FieldsetLegend } from 'components/Form/Input/FieldsetLegend/FieldsetLegend';
import { useSchemableFormFileUploadContext } from 'components/SchemableForm/contexts/SchemableFormFileUploadContext/SchemableFormFileUploadContext';
import { ExternalFilesList } from 'components/SchemableForm/fields/components/ExternalFilesList/ExternalFilesList';
import { Translate } from 'components/Translate/Translate';

import { FileUploadValue } from '../FileUploadField';

type FileUploadFieldEditProps = {
  field: ui.FileUploadField;
  fieldId: string;
  isReadOnly: boolean;
  variant?: 'large';
  value: FileUploadValue;
};

export const FileUploadFieldEdit = ({ field, fieldId, isReadOnly, variant, value }: FileUploadFieldEditProps) => {
  const formState = useFormState();
  const { t } = useTranslation();
  const fileUploadContextValue = useSchemableFormFileUploadContext();
  const { uploadFile } = fileUploadContextValue || {};
  const { setValue } = useFormContext();
  const { service } = useErrorContext<WebEDIError>();
  const error = service.useErrorById(fieldId);

  const [uploadError, setUploadError] = useState<string | null>(null);

  const loader = useCallback(
    async (file: File): Promise<LoaderResult<File>> => {
      if (!uploadFile) {
        throw new Error('File upload is not available');
      }
      setUploadError(null);
      const { fileName } = await uploadFile(file);
      return { data: { ...file, name: fileName } };
    },
    [uploadFile],
  );

  const handleLoad = useCallback(
    (serverFile: File, localFile: File) => {
      setValue(
        fieldId,
        {
          file_name: serverFile.name,
          original_name: localFile.name,
          mime_type: localFile.type,
        },
        { shouldDirty: true, shouldValidate: formState.isSubmitted },
      );
    },
    [fieldId, formState.isSubmitted, setValue],
  );

  const handleError = useCallback((error: Error, _file: File) => {
    logError(error);
    setUploadError(error.message);
  }, []);

  const handleClear = useCallback(() => {
    setValue(fieldId, null);
  }, [fieldId, setValue]);

  const isLargeVariant = variant === 'large';
  const validationError =
    isLargeVariant && error?.description ? { message: error.description, type: error.type ?? 'error' } : undefined;
  const inlineErrors = uploadError ? { message: uploadError, type: 'error' } : validationError;

  return (
    <div className="space-y-2">
      <FieldsetLegend
        label={field.label}
        description={field.description ? <Translate i18nKey={field.description} /> : undefined}
        variant={isLargeVariant ? 'large' : 'default'}
        required={field.modality === 'MANDATORY'}
      />

      <ExternalFilesList
        externalFiles={field.type_config.external_files}
        action={{
          label: t('common:form.externalAction.openFile'),
          onClick: (file) => window.open(file.url, '_blank'),
          RightIcon: ArrowTopRightOnSquareIcon,
        }}
      />

      {!isReadOnly && (
        <FileInput
          loader={loader}
          onLoad={handleLoad}
          onError={handleError}
          onClear={handleClear}
          defaultFilename={value?.original_name || undefined}
          errors={inlineErrors}
        />
      )}
    </div>
  );
};
